import { Component, inject, input, OnInit, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IPermissionGroup, PermissionGroup } from '../../models/permission-group.model';
import { TreeNode } from 'primeng/api';
import { TranslateModule } from '@ngx-translate/core';
import { TreeModule } from 'primeng/tree';
import { IPermission } from '../../../permissions/dtos/responses/permission.response';
import { Logger } from '../../../../../core/utilities/logging/logger';
import { ITypedLookup } from '../../../common/models/permission-marker-lookup';
import { Permission } from '../../../permissions/models/permission.model';
import { IPermissionTreeOutput } from '../../models/permission-tree-output.model';
import { IAssignRevokePermissionsClient } from '../../clients/i-assign-revoke-permissions.client';
import { AssignRevokePermissionsClientProvider } from '../../clients/assign-revoke-permissions-client.provider';
import { handleApiErrors } from '../../../../../core/data-access/http-client/interceptors/error.interceptor';
import { ToastService } from '../../../../../../trash/shared/components/toasters/Toast.service';
import { forkJoin } from 'rxjs';
import { LoaderService } from '../../../../../../trash/shared/components/loader/loader.service';

@Component({
  selector: 'app-permissions-tree',
  standalone: true,
  imports: [CommonModule, TranslateModule, TreeModule],
  templateUrl: './permissions-tree.component.html',
  styleUrls: ['./permissions-tree.component.scss']
})
export class PermissionsTreeComponent implements OnInit {
  client: IAssignRevokePermissionsClient = inject(AssignRevokePermissionsClientProvider).getClient();
  loaderService = inject(LoaderService);
  toastService = inject(ToastService);

  assignedPermissionGroupsEndPoint = input<string>();
  permissionsChanged = output<IPermissionTreeOutput>();

  tree: TreeNode<ITypedLookup<string>>[] = [];
  selectedNodes: TreeNode<ITypedLookup<string>>[] = [];
  assignedPermissions: IPermission[] = [];

  constructor() {}

  ngOnInit(): void {
    this.loaderService.show();
    forkJoin({
      permissionGroups: this.client.permissionGroups(),
      assignedPermissionGroups: this.client.assignedPermissionGroups(this.assignedPermissionGroupsEndPoint() || '')
    }).subscribe({
      next: ({ permissionGroups, assignedPermissionGroups }) => {
        this.loadTree(permissionGroups, assignedPermissionGroups);
        this.loaderService.hide();
      },
      error: (x) => {
        handleApiErrors(x, this.toastService);
        this.loaderService.hide();
      }
    });
  }

  loadTree(permissions: IPermissionGroup[], assignedPermissions: IPermissionGroup[]) {
    this.tree = PermissionGroup.createAsTreeNodes(permissions);
    this.selectedNodes = PermissionGroup.createAsFlattenedTree(assignedPermissions);
    this.assignedPermissions = this.selectedNodes
      .map((node) => node.data)
      .filter((data): data is IPermission => !!data && data.type === Permission.PermissionType);
  }

  onNodeSelect(): void {
    this.permissionsChanged.emit(this.getOutput());
  }

  onNodeUnselect(): void {
    this.permissionsChanged.emit(this.getOutput());
  }

  getSelectedPermissions(): IPermission[] {
    return this.selectedNodes
      .map((node) => node.data)
      .filter((data): data is IPermission => !!data && data.type === Permission.PermissionType);
  }

  getOutput(): IPermissionTreeOutput {
    const output: IPermissionTreeOutput = {
      permissionIdsToAssign: this.getPermissionsToAssign(),
      permissionIdsToRevoke: this.getPermissionsToRevoke()
    };

    Logger.info('Permission Tree Output Generated', output);

    return output;
  }

  private getPermissionsToAssign(): string[] {
    const selectedPermissions = this.getSelectedPermissions();
    const assignedPermissionIds = new Set(this.assignedPermissions.map((p) => p.id));
    return selectedPermissions
      .filter((permission) => !assignedPermissionIds.has(permission.id))
      .map((permission) => permission.id);
  }

  private getPermissionsToRevoke(): string[] {
    const selectedIds = new Set(this.getSelectedPermissions().map((p) => p.id));
    return this.assignedPermissions
      .filter((permission) => !selectedIds.has(permission.id))
      .map((permission) => permission.id);
  }
}
